import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ShiftType } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import * as kpiState from '../../state';
import { useQaqcJobPhaseProgress } from '../../hooks/useQaqcJobPhaseProgress';
import { accordionStyles, accordianDetailsStyles, getAccordionSummaryStyles } from './styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { textButtonStyle } from '@/theme';

export const JobPhaseSection = () => {
  const setQaqcPanelOpen = useSetAtom(kpiState.qaqcPanelOpen);
  const setQaqcPanelOpenIntent = useSetAtom(kpiState.qaqcPanelOpenIntent);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const { loading, progress, hasSavedJobPhaseForm } = useQaqcJobPhaseProgress();
  const [expanded, setExpanded] = useState(true);

  const completedLabel = loading ? (
    <Skeleton width={72} sx={{ display: 'inline-block' }} />
  ) : (
    `${progress.completed}/${progress.total}`
  );

  const openPanel = () => {
    setQaqcPanelOpenIntent(hasSavedJobPhaseForm ? 'view' : 'fill');
    setQaqcPanelOpen(true);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disableGutters
      sx={{ ...accordionStyles, mt: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={getAccordionSummaryStyles(expanded)}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, fontSize: '1.4rem', color: abyssColors.primary[500] }}
          >
            Job phase
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ ...accordianDetailsStyles, pt: 2, pb: 2 }}>
        <Box
          sx={{
            border: `1px solid ${abyssColors.neutral[200]}`,
            borderRadius: 1,
            p: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            gap={2}
            sx={{
              border: `1px solid ${abyssColors.primary[200]}`,
              borderRadius: 1,
              p: 2,
              backgroundColor: abyssColors.primary[50],
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
              {hasSavedJobPhaseForm ? (
                <AbyssIcon iconName="check-circle-success-filled" size="lg" />
              ) : (
                <AbyssIcon iconName="document" size="lg" />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, fontSize: '1.4rem', color: abyssColors.primary[500] }}
                >
                  {selectedShift === ShiftType.Night ? 'Night Shift' : 'Day Shift'}
                </Typography>
                <Typography
                  variant="body2"
                  fontSize="1.2rem"
                  sx={{ color: abyssColors.primary[400] }}
                >
                  {completedLabel} Fields completed
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              size="medium"
              onClick={openPanel}
              sx={{ ...textButtonStyle, px: 2, height: '2.6rem' }}
            >
              {hasSavedJobPhaseForm ? 'View Job Phase' : 'Fill Job Phase'}
            </Button>
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
